/**
 * Platform-wide formatting utilities.
 *
 * TIMESTAMP STANDARD:
 * - All timestamps are stored in UTC in the database
 * - The backend serializes them with the 'Z' suffix (ISO 8601)
 * - All display functions here convert to the user's local (browser) timezone
 *   via the native Intl / Date APIs — no manual offset math needed
 *
 * Use these shared functions instead of inline formatting in components.
 */

// ---------------------------------------------------------------------------
// Date / Time
// ---------------------------------------------------------------------------

/**
 * Format a UTC date string as a short date in the user's local timezone.
 * Example: "Mar 12, 2026"
 */
export function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format a calendar date that carries no meaningful time, such as a contract's start or end
 * date. The API serializes these as midnight UTC, so converting to the browser's timezone would
 * show the previous day to anyone west of UTC; this keeps the calendar day as stored.
 * Example: "Jun 30, 2026"
 */
export function formatDateOnly(dateString: string): string {
  const [year, month, day] = dateString.slice(0, 10).split('-').map(Number);
  return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format a UTC date string as date + time in the user's local timezone.
 * Example: "Mar 12, 2026, 9:30 PM"
 */
export function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Format a UTC date string as date + time with timezone abbreviation.
 * Example: "Mar 12, 2026, 9:30 PM EST"
 */
export function formatDateTimeWithTz(dateString: string): string {
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  });
}

/**
 * Format a UTC date string as a relative time string.
 * Examples: "Just now", "5m ago", "3h ago", "2d ago", or falls back to short date.
 */
export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Format a month label from a date string.
 * Example: "January 2026"
 */
export function formatMonthYear(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
}

// ---------------------------------------------------------------------------
// Timezone-aware conversion (for inputs with a separate timezone selector)
// ---------------------------------------------------------------------------

interface DateParts { year: string; month: string; day: string; hour: string; minute: string; second: string }

function getDateParts(date: Date, timezone: string, includeSeconds: boolean): DateParts {
  const options: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  };
  if (includeSeconds) options.second = '2-digit';

  const raw = new Intl.DateTimeFormat('en-US', options).formatToParts(date);
  const map: Record<string, string> = {};
  for (const { type, value } of raw) map[type] = value;

  return {
    year: map.year ?? '0000',
    month: map.month ?? '01',
    day: map.day ?? '01',
    hour: map.hour ?? '00',
    minute: map.minute ?? '00',
    second: map.second ?? '00',
  };
}

/**
 * Convert a datetime-local input value (timezone-naive) into a UTC ISO string,
 * interpreting the entered time as belonging to the given IANA timezone.
 *
 * Example: localTimeInTzToUtc("2026-04-15T13:00", "America/New_York")
 *          → "2026-04-15T17:00:00.000Z"  (1 PM EDT = 5 PM UTC)
 */
export function localTimeInTzToUtc(naiveDatetime: string, timezone: string): string {
  const tIdx = naiveDatetime.indexOf('T');
  const datePart = naiveDatetime.slice(0, tIdx);
  const timePart = naiveDatetime.slice(tIdx + 1);
  const [year, month, day] = datePart.split('-').map(Number) as [number, number, number];
  const [hours, minutes] = timePart.split(':').map(Number) as [number, number];

  // Treat the entered numbers as a UTC instant (just as a reference point)
  const refUtc = new Date(Date.UTC(year, month - 1, day, hours, minutes));

  // Read what the target timezone's clock shows at that reference UTC instant
  const parts = getDateParts(refUtc, timezone, true);

  let tzHour = parseInt(parts.hour, 10);
  if (tzHour === 24) tzHour = 0;

  const tzAsUtc = Date.UTC(
    parseInt(parts.year, 10),
    parseInt(parts.month, 10) - 1,
    parseInt(parts.day, 10),
    tzHour,
    parseInt(parts.minute, 10),
    parseInt(parts.second, 10),
  );

  // offset = (what TZ clock shows, as UTC numbers) − actual UTC
  // Correct UTC = entered time (as UTC numbers) − offset
  const offsetMs = tzAsUtc - refUtc.getTime();
  return new Date(refUtc.getTime() - offsetMs).toISOString();
}

/**
 * Convert a UTC ISO string to a datetime-local value in the given IANA timezone.
 * Used to pre-fill a <input type="datetime-local"> with the correct wall-clock time.
 *
 * Example: utcToLocalTimeInTz("2026-04-15T17:00:00.000Z", "America/New_York")
 *          → "2026-04-15T13:00"  (5 PM UTC = 1 PM EDT)
 */
export function utcToLocalTimeInTz(utcIsoString: string, timezone: string): string {
  const utcDate = new Date(utcIsoString);
  const parts = getDateParts(utcDate, timezone, false);

  let hour = parseInt(parts.hour, 10);
  if (hour === 24) hour = 0;

  return `${parts.year}-${parts.month}-${parts.day}T${String(hour).padStart(2, '0')}:${parts.minute}`;
}

// ---------------------------------------------------------------------------
// Duration
// ---------------------------------------------------------------------------

export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
}

/**
 * Total length in seconds of a stored course duration. `seconds` is the 0–59
 * remainder on top of `minutes`, never a total (BPE-771).
 */
export function totalDurationSeconds(minutes: number, seconds = 0): number {
  return minutes * 60 + seconds;
}

/** Clock-style "m:ss" duration for short-form video (Microlearning). */
export function formatClockDuration(minutes: number, seconds = 0): string {
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export interface CourseDurationFields {
  durationMinutes?: number | null;
  /** The 0–59 remainder on top of `durationMinutes`; absent where the source carries none. */
  durationSeconds?: number | null;
  variantKinds?: readonly string[] | null;
}

/**
 * A course's duration as every screen should show it (BPE-771): Microlearning
 * is short-form video, so it reads as clock time ("3:46"), the same as its
 * detail page and the video player; every other kind reads in minutes, in the
 * caller's own minutes style.
 */
export function formatCourseDuration(
  course: CourseDurationFields,
  formatMinutes: (minutes: number) => string = formatDuration,
): string {
  const minutes = course.durationMinutes ?? 0;
  if (course.variantKinds?.includes('Microlearning')) {
    return formatClockDuration(minutes, course.durationSeconds ?? 0);
  }
  return formatMinutes(minutes);
}

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

export function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    active: 'success',
    inactive: 'neutral',
    on_leave: 'warning',
    terminated: 'error',
    trial: 'info',
    deactivated: 'warning',
    cancelled: 'error',
    completed: 'success',
    in_progress: 'info',
    not_started: 'neutral',
    overdue: 'error',
  };
  return colors[status] || 'neutral';
}
